import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Role, Permission } from "@/lib/rbac";
import { hasPermission } from "@/lib/rbac";

export interface OrgRoleResult {
  orgId: string | null;
  role: Role | null;
  isLoading: boolean;
  can: (permission: Permission) => boolean;
}

export function useOrgRole(): OrgRoleResult {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["org-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get org membership
      const { data: member } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();

      if (!member) return null;

      // Get role from org_roles
      const { data: roleRow } = await supabase
        .from("org_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("org_id", member.org_id)
        .maybeSingle();

      return {
        orgId: member.org_id,
        role: (roleRow?.role as Role) ?? "viewer",
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60_000,
  });

  const role = data?.role ?? null;
  const orgId = data?.orgId ?? null;

  return {
    orgId,
    role,
    isLoading,
    can: (permission: Permission) => hasPermission(role, permission),
  };
}
